<script lang="ts">
  import { app } from './appState.svelte';
  import { coverStyle } from './covers';
  import { thumbCache } from '../ink/thumbnail';
  import type { Notebook, Page } from '../store/types';

  let notebooks = $state<Notebook[]>([]);
  let pages = $state<(Page & { notebookTitle: string })[]>([]);
  /** Row id (or 'empty') whose Delete forever is awaiting inline confirmation. */
  let confirmKey = $state<string | null>(null);
  let isEmpty = $derived(notebooks.length === 0 && pages.length === 0);

  async function refresh() {
    const t = await app.repo!.listTrash();
    notebooks = t.notebooks;
    pages = t.pages;
  }
  $effect(() => { void refresh(); });

  // Live refresh: remote deletes/restores/purges land via sync — same
  // 'applied' pattern as the Library list.
  $effect(() => {
    const sync = app.syncEngine;
    if (!sync) return;
    const onApplied = () => void refresh();
    sync.on('applied', onApplied);
    return () => sync.off('applied', onApplied);
  });

  /** Svelte action: render a page thumbnail into the placeholder (trash lists are small — eager is fine). */
  function thumb(el: HTMLElement, page: Page) {
    void (async () => {
      const [strokes, boxes] = await Promise.all([
        app.repo!.getActiveStrokes(page.id),
        app.repo!.getActiveTextBoxes(page.id),
      ]);
      el.replaceChildren(thumbCache.getOrRender(page, strokes, boxes, app.theme, 72, 96));
    })();
  }

  async function restoreNotebook(id: string) {
    await app.repo!.restoreNotebook(id);
    await refresh();
  }
  async function restorePage(id: string) {
    await app.repo!.restorePage(id);
    await refresh();
  }
  async function purgeNotebook(id: string) {
    await app.repo!.purgeNotebook(id);
    confirmKey = null;
    await refresh();
  }
  async function purgePage(id: string) {
    await app.repo!.purgePage(id);
    confirmKey = null;
    await refresh();
  }
  async function emptyTrash() {
    for (const nb of notebooks) await app.repo!.purgeNotebook(nb.id);
    for (const p of pages) await app.repo!.purgePage(p.id);
    confirmKey = null;
    await refresh();
  }

  const when = (ts: number | undefined) => (ts ? new Date(ts).toLocaleDateString() : '');
</script>

<div class="trash-view">
  <header>
    <button class="back" onclick={() => (app.route = { screen: 'library' })} title="Back">←</button>
    <h1>Trash</h1>
    <div class="spacer"></div>
    {#if !isEmpty}
      {#if confirmKey === 'empty'}
        <span class="really">Really delete everything?</span>
        <button class="danger" onclick={emptyTrash}>Yes</button>
        <button onclick={() => (confirmKey = null)}>No</button>
      {:else}
        <button class="danger-ghost" onclick={() => (confirmKey = 'empty')}>Empty trash</button>
      {/if}
    {/if}
  </header>

  {#if isEmpty}
    <div class="empty"><p>Trash is empty.</p></div>
  {/if}

  {#if notebooks.length}
    <h2>Notebooks</h2>
    <div class="rows">
      {#each notebooks as nb (nb.id)}
        <div class="row notebook-row">
          <div class="cover" style="background:{coverStyle(nb.cover)}"></div>
          <div class="meta">
            <span class="title">{nb.title}</span>
            <span class="sub">Deleted {when(nb.deletedAt)}</span>
          </div>
          <div class="actions">
            {#if confirmKey === nb.id}
              <span class="really">Really delete?</span>
              <button class="danger" onclick={() => purgeNotebook(nb.id)}>Yes</button>
              <button onclick={() => (confirmKey = null)}>No</button>
            {:else}
              <button onclick={() => restoreNotebook(nb.id)}>Restore</button>
              <button class="danger-ghost" onclick={() => (confirmKey = nb.id)}>Delete forever</button>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if pages.length}
    <h2>Pages</h2>
    <div class="rows">
      {#each pages as p (p.id)}
        <div class="row page-row">
          <div class="thumb" use:thumb={p} style="aspect-ratio: {p.kind === 'fixed' ? '210 / 297' : '4 / 3'}"></div>
          <div class="meta">
            <span class="title">Page from “{p.notebookTitle}”</span>
            <span class="sub">Deleted {when(p.deletedAt)}</span>
          </div>
          <div class="actions">
            {#if confirmKey === p.id}
              <span class="really">Really delete?</span>
              <button class="danger" onclick={() => purgePage(p.id)}>Yes</button>
              <button onclick={() => (confirmKey = null)}>No</button>
            {:else}
              <button onclick={() => restorePage(p.id)}>Restore</button>
              <button class="danger-ghost" onclick={() => (confirmKey = p.id)}>Delete forever</button>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .trash-view { padding: 20px; max-width: 760px; margin: 0 auto; }
  header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  h1 { font-size: 22px; }
  h2 { font-size: 14px; opacity: 0.7; margin: 22px 0 8px; }
  .spacer { flex: 1; }
  .empty { text-align: center; opacity: 0.6; padding: 60px 0; }
  .rows { display: flex; flex-direction: column; gap: 10px; }
  .row {
    display: flex; align-items: center; gap: 14px; padding: 10px 14px;
    background: var(--surface); border-radius: var(--radius-panel); box-shadow: var(--shadow-soft);
  }
  .cover { width: 40px; height: 54px; border-radius: 6px; flex: none; }
  .thumb {
    width: 40px; flex: none; background: var(--paper);
    border: 1px solid var(--hairline); border-radius: 4px;
    display: grid; place-items: center; overflow: hidden;
  }
  .thumb :global(canvas) { display: block; max-width: 100%; max-height: 100%; }
  .meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
  .title { font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { font-size: 12px; opacity: 0.6; }
  .actions { display: flex; align-items: center; gap: 8px; flex: none; flex-wrap: wrap; justify-content: flex-end; }
  .really { font-size: 13px; opacity: 0.8; }
  button {
    border: 1px solid var(--hairline); background: var(--paper); color: var(--ink-primary);
    border-radius: var(--radius-panel); padding: 8px 12px; min-height: 40px; cursor: pointer; font-size: 13px;
  }
  .back { border: none; background: var(--surface); min-width: 44px; min-height: 44px; font-size: 17px; }
  .danger { background: #c0564a; border-color: #c0564a; color: #fff; }
  .danger-ghost { color: #c0564a; }
</style>
